<template>
	<div
		id="app"
		:class="{
			subMenuClick: subMenuClick,
			hasHeader: showHeader,
			hasFooter: showFooter,
			showingModal: showingModal,
			fullMenu: fullMenu && !isPublic,
			showMenu: mobileMenu,
			hasMessage: hasVisibleTopMessage,
		}"
		v-if="showPage || isPublic">
		<AppHeader
			:class="headerClasses"
			v-if="showHeader"
			:siteName="siteName"
			@fullMenu="setFullMenu"
			:fullMenu="fullMenu"
			@mobileMenu="toggleMobileMenu"
			@menuLinkClick="menuLinkClick"
			@showHelp="showHelp" />
		<div class="wrapper">
			<TopMessage
				:message="globalMessage"
				:colour="globalMessageColour"
				:meta="globalMessageMeta"
				@resendConfirmEmail="resendConfirmEmail"
				@visibility-change="setTopMessageVisible"
				@close="closeTopMessage"
				v-if="hasTopMessage" />
			<Suspense>
				<router-view :key="routerViewKey" />
				<template #fallback>
					<div class="internal-loading-spinner">
						<LoadingSpinner text="Loading..." :position="'internal'" />
					</div>
				</template>
			</Suspense>
			<Pupup
				@close="closePupup"
				v-if="showWelcome && !isPublic"
				:showClose="true"
				:title="welcomeIsHelp ? 'Help' : 'Let\'s get you started...'"
				:width="1200">
				<template v-slot:content>
					<div class="welcome-panels-wrap">
						<div class="welcome-panels">
							<div class="welcome-panel" v-if="pupupPage === 1">
								<p v-if="welcomeIsHelp && !showWelcomeOpenerMessage" class="opener highlighted">
									Stuck for collab ideas? Not sure how to make the most out of PartnerUp?
									<a href="https://calendly.com/partneruphub/30min" target="_blank">Have a call with Amber</a>
								</p>
								<p v-if="showWelcomeOpenerMessage" class="opener highlighted" v-html="showWelcomeOpenerMessage" />

								<div class="list-items">
									<div class="list-item">
										<Icon type="matches" class="svg" />
										<h2>View your matches:</h2>
										<p>This is where you can see your matches and send a request to connect.</p>
										<hr />
									</div>

									<div class="list-item">
										<Icon type="requests" class="svg" />
										<h2>View your connection requests:</h2>
										<p>
											When a brand sends you a request to connect, you'll see them here. When you send a connection request,
											you'll also see them here.
										</p>
										<hr />
									</div>

									<div class="list-item">
										<Icon type="connections" class="svg" />
										<h2>View your connections:</h2>
										<p>
											When another brand confirms your connection request or you confirm a brand's connection request, you will
											see the details here.
										</p>
										<hr />
									</div>

									<div class="list-item">
										<Icon type="x" class="svg" />
										<h2>View your declined brands:</h2>
										<p>Should you want to revisit any declined connections and/or matches, they will be found here.</p>
										<hr />
									</div>
									<div class="list-item">
										<Icon type="pitch" class="svg" />
										<h2>Opportunities Area</h2>
										<p>
											Discover current collab opportunities from other brands and Pitch to collab with them. Or feature your own
											upcoming opportunities and have brands Pitch to you.
										</p>
										<hr />
									</div>

									<div class="list-item">
										<Icon type="ideas" class="svg" />
										<h2>Collab Ideas</h2>
										<p>Need some fresh collaboration ideas? Find them here with how-to guides, tips and templates.</p>
										<hr />
									</div>

									<div class="list-item">
										<Icon type="profile" class="svg" />
										<h2>Your brand details</h2>
										<p>
											Please keep your brand details up to date to ensure your matches are relevant. This is where you can
											update your brand partner preferences too.
										</p>
									</div>
								</div>
							</div>

							<div class="welcome-panel panel2" v-if="pupupPage === 2">
								<h2 class="brown">Using PartnerUp</h2>

								<h3>How to send a connection request to a brand:</h3>
								<p>
									Click on the matches icon and when you see a brand you like, hit the 'connect' button. We'll send them an email
									letting them know you're interested.<br />
									If they accept, you'll be sent a notification with their details and vice versa, so you can immediately start
									planning your cool collab. <br />
									If the other brand decides the opportunity is not right for them, you'll also receive a notification to let you
									know.
								</p>

								<h3>Receiving a connection request from a brand</h3>
								<p>
									If a brand sends you a connection request (yay!), you'll be notified by email and their details will appear in the
									'request' section of the dashboard.<br />
									If you choose to accept, click the accept button and you'll be shared the brands details immediately. They will
									also be notified by email.<br />
									If you choose to decline (no sweat), click the relevant button and we will send a nice reply on your behalf.
								</p>

								<h3>How to feature your brand in the Opportunities Area</h3>
								<p>
									In the Opportunities Area click 'Create an Opportunity'. Complete the form and click 'Preview / Send for Approval.
									Once you are happy click 'Send for Approval'. Once your Opportunity has been approved (normally within 24 hours)
									you'll receive an email and all the details will be shown in the Opportunities Area for other brands to see.
								</p>

								<h3>Pitch to collab with another brand in the Opportunities Area</h3>
								<p>
									In the Opportunities Area, you can view collaboration opportunities from other brands. When you see one you like,
									click 'Pitch to Collab'. Tell the other brand why you'd be a great fit for their collab. If the other brand
									accepts, they will be in touch directly you.
								</p>
							</div>
						</div>

						<div class="video-guides" @click="loadVideos">
							<img src="https://img.youtube.com/vi/KPa8p40NIFc/maxresdefault.jpg" alt="How to get started on PartnerUp" />
							<p>View our video guides here</p>
						</div>

						<div class="buttons">
							<span class="button" v-if="pupupPage > 1" @click="--pupupPage">Previous</span>

							<span class="button" v-if="pupupPage < pupupPages" @click="++pupupPage">Next</span>

							<span class="button" v-if="pupupPage === pupupPages" @click="closePupup">Finish</span>
						</div>
					</div>
				</template>
			</Pupup>
			<PitchBrandPopup />
			<BrandCardPopup />
			<CollabHelpNeverDone />
			<CollabHelp v-if="collabHelpPopup.show" @close="closeCollabHelpPopup" @take-quiz="handleCollabHelpQuiz" />
			<IdeaQuiz v-if="showGlobalIdeaQuiz" @close="closeGlobalIdeaQuiz" />
			<CollabPupup :slug="selectedInspiration" v-if="!!selectedInspiration" @close="closeSelectedInspiration" />
			<Pupup
				v-if="showingSubscriptionPupup && !isPublic"
				:width="featureOnly ? 800 : 1200"
				@close="closeSubscriptionPupup"
				:shuffleUp="true"
				:hideOuter="hideSubscriptionPupup"
				style="z-index: 1000 !important">
				<template v-slot:content>
					<Subscriptions
						:isOverlay="true"
						:paymentRedirect="subscriptionPupupPaymentRedirect"
						:title="subscriptionPupupTitle"
						:featureOnlyOverride="subscriptionPupupFeatureOnly"
						@close="closeSubscriptionPupup"
						@hidePupup="hideSubscriptionPupup = true" />
				</template>
			</Pupup>
			<BrandConnect
				v-if="brandConnectModal.show"
				:connecteeId="brandConnectModal.connecteeId"
				:initialData="brandConnectModal.initialData"
				@close="handleBrandConnectClose"
				@preview-feature="handleBrandConnectPreview" />
			<updates-popup v-if="showUpdatesPopup" @showHelp="showHelp" @close="showUpdatesPopup = false" :content="updatesPopupContent" />
			<DatesOfInterestModal v-if="!isPublic" />
			<BrandActionOverlays />
			<Pupup v-if="showFeatureTsCs" :showClose="true" :title="'Opportunity Terms & Conditions'" :width="800" @close="toggleFeatureTsCs">
				<template v-slot:content>
					<ol class="feature-terms-list">
						<li>
							<strong>Service Overview</strong><br />PartnerUp Limited ("PartnerUp") provides a platform for businesses to showcase
							their opportunities to potential brand partners for collaboration and partnership purposes.
						</li>
						<li>
							<strong>Terms and Conditions</strong>
							<ul>
								<li>
									<strong>2.1 Listing Requirements</strong><br />The Client agrees to:
									<ul>
										<li>Provide accurate and current information for the Opportunity.</li>
										<li>Supply high-quality digital assets that meet PartnerUp's specifications.</li>
										<li>
											Maintain the accuracy of the opportunity throughout the contract period. Please contact us at
											<a href="mailto:hello@partneruphub.com">hello@partneruphub.com</a> to make changes to your Opportunity.
										</li>
									</ul>
								</li>
								<li>
									<strong>2.2 Early Termination</strong>
									<ul>
										<li>
											The agreement may be terminated before the specified period (3 or 6 months) only with written consent from
											both parties.
										</li>
										<li>No refund is provided if early termination is agreed.</li>
									</ul>
								</li>
							</ul>
						</li>
						<li>
							<strong>Agreement</strong><br /><strong>There is no guarantee that a brand will respond to your Opportunity.</strong> By
							submitting and making payment for the Opportunity, you acknowledge that you have read, understood, and agree to these
							terms and conditions.
						</li>
					</ol>
				</template>
			</Pupup>

			<Pupup
				:showClose="true"
				:title="showingNotificationsPupupTitle"
				:width="1200"
				@close="closeNotificationsPupup"
				v-if="showingNotificationsPupup">
				<template v-slot:content>
					<Notifications :modal="true" :modalSources="showingNotificationsPupupSources" />
				</template>
			</Pupup>
			<BrandConnectPreview />
		</div>
		<Videos v-if="showVideos" @close="closeVideos" />
		<Slack v-if="showingSlackPupup" />
		<AppFooter v-if="showFooter" :siteName="siteName" :class="footerClasses" />
		<Loading :text="loadingText" v-if="loadingText != ''" />
		<welcome-popup
			v-if="showWelcomePopup"
			:type="showWelcomePopup"
			@showHelp="showHelp"
			@close="showWelcomePopup = ''"
			:subtitle="showWelcomeOpenerMessage" />
		
		<div :class="{ 'posthog-menu-item': true, show: showPostHogEvent }" v-if="showPostHog">
			<span @click="showPostHogEvent = !showPostHogEvent"></span>
			<FormInput
				v-model="posthogEventText"
				:noMarginBottom="true"
				placeholder="Small unique description"
				class="posthog-event-text"
				:required="false"
				@aKeyup-enter="sendPostHogEvent"
				@aKeyup-esc="showPostHogEvent = false" />
		</div>

		<!-- Feedback Widget -->
		<FeedbackWidget />

		<!-- Vue3 Confirm Dialog Component -->
		<vue3-confirm-dialog ref="confirmDialog"></vue3-confirm-dialog>
	</div>
</template>

<script>
import { ref, computed, watch, onBeforeMount, Suspense } from "vue";
import { useRoute } from "vue-router";
import { useVueInstance, useNotifications } from "./composables/useVueInstance";
import AppHeader from "./components/AppHeader.vue";
import AppFooter from "./components/AppFooter.vue";
import posthog from "posthog-js";
import FormInput from "./components/Form/Input.vue";
import TopMessage from "./components/Display/TopMessage.vue";
import Pupup from "./components/Display/Pupup.vue";
import CollabPupup from "./components/Popups/CollabPupup.vue";
import BrandCardPopup from "./components/Popups/BrandCardPopup.vue";
import PitchBrandPopup from "./components/Popups/PitchBrandPopup.vue";
import CollabHelp from "./components/Popups/CollabHelp.vue";
import CollabHelpNeverDone from "./components/Popups/CollabHelpNeverDone.vue";
import IdeaQuiz from "./components/Popups/IdeaQuiz.vue";
import BrandConnect from "./components/Display/BrandConnect.vue";
import BrandActionOverlays from "./components/Display/BrandActionOverlays.vue";
import BrandConnectPreview from "./components/Display/BrandConnectPreview.vue";
import DatesOfInterestModal from "./components/Popups/DatesOfInterestModal.vue";
import Notifications from "./views/Account/Notifications.vue";
import InstructionTips from "./components/InstructionTips.vue";
import Subscriptions from "./views/Account/Subscriptions.vue";
import Videos from "./components/Videos.vue";
import Icon from "./components/Display/BaseIcon.vue";
import Slack from "./components/Display/Slack.vue";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import general from "./utils/general";
import {
	buildConnectActionPayload,
	getActionBase,
	getBrandActionMessageTitle,
	isImmediateBrandAction,
	shouldClosePopupForAction,
	toActionRequest,
} from "./utils/brandActions";
import { isBrandsPopupSource, resolvePopupSource } from "./utils/popupSources";
import ApiService from "./services/api.service";
import { useBrandPopupHandlers } from "./composables/useBrandPopupHandlers";
import WelcomePopup from "./components/Popups/WelcomePopup.vue";
import UpdatesPopup from "./components/Popups/UpdatesPopup.vue";
import FeedbackWidget from "./components/FeedbackWidget.vue";
const { TokenService } = require("./services/storage.service");
import Loading from "./components/LoadingSpinner.vue";
export default {
	name: "app",
	components: {
		AppHeader,
		AppFooter,
		FormInput,
		TopMessage,
		InstructionTips,
		Pupup,
		CollabPupup,
		BrandCardPopup,
		PitchBrandPopup,
		CollabHelp,
		CollabHelpNeverDone,
		IdeaQuiz,
		BrandConnect,
		BrandActionOverlays,
		BrandConnectPreview,
		DatesOfInterestModal,
		Icon,
		Videos,
		Subscriptions,
		Slack,
		Notifications,
		WelcomePopup,
		LoadingSpinner,
		Suspense,
		Loading,
		UpdatesPopup,
		FeedbackWidget,
	},
	setup() {
		const { store } = useVueInstance();
		const { success, error } = useNotifications();
		const route = useRoute();

		// Data properties
		const showPage = ref(false);
		const subMenuClick = ref(false);
		const pupupPage = ref(1);
		const pupupPages = ref(2);
		const welcomeIsHelp = ref(false);
		const needsLoginCheck = ref(true);
		const showWelcomeOpenerMessage = ref("");
		const posthogEventText = ref("");
		const showPostHogEvent = ref(false);
		const hideSubscriptionPupup = ref(false);
		const showWelcomePopup = ref("");
		const showUpdatesPopup = ref(false);
		const showGlobalIdeaQuiz = ref(false);
		const topMessageVisible = ref(false);
		const updatesPopupContent = ref({ title: "No more credits! 🎉", message: "You can now send requests to brands without using credits." });

		// Vuex State -> Computed
		const loadingText = computed(() => store.state.general.loadingText);
		const showHeader = computed(() => store.state.general.showHeader);
		const showFooter = computed(() => store.state.general.showFooter);
		const showingModal = computed(() => store.state.general.showingModal);
		const siteName = computed(() => store.state.general.siteName);
		const fullMenu = computed(() => store.state.general.fullMenu);
		const mobileMenu = computed(() => store.state.general.mobileMenu);
		const globalMessage = computed(() => store.state.general.globalMessage);
		const globalMessageColour = computed(() => store.state.general.globalMessageColour);
		const globalMessageMeta = computed(() => store.state.general.globalMessageMeta);
		const subscription = computed(() => store.state.general.subscription);
		const showWelcome = computed(() => store.state.general.showWelcome);
		const brand = computed(() => store.state.general.brand);
		const brandConnectModal = computed(() => store.state.general.brandConnectModal || {});
		const brandCardPopup = computed(() => store.state.general.brandCardPopup || {});
		const pitchBrandPopup = computed(() => store.state.general.pitchBrandPopup || {});
		const collabHelpPopup = computed(() => store.state.general.collabHelpPopup || {});
		const brandCardPopupEvent = computed(() => store.state.general.brandCardPopupEvent);
		const brandConnectEvent = computed(() => store.state.general.brandConnectEvent);
		const brandActionUi = computed(() => store.state.general.brandActionUi || {});
		const brandActionEvent = computed(() => store.state.general.brandActionEvent);
		const showFeatureTsCs = computed(() => store.state.general.showFeatureTsCs);
		const selectedInspiration = computed(() => store.state.general.selectedInspiration);
		const checkMatchRunUpdateMessage = computed(() => store.state.general.checkMatchRunUpdateMessage);
		const userCreated = computed(() => store.state.general.userCreated);
		const showVideos = computed(() => store.state.general.showVideos);
		const showingSubscriptionPupup = computed(() => store.state.general.showingSubscriptionPupup);
		const subscriptionPupupTitle = computed(() => store.state.general.subscriptionPupupTitle);
		const subscriptionPupupPaymentRedirect = computed(() => store.state.general.subscriptionPupupPaymentRedirect);
		const subscriptionPupupFeatureOnly = computed(() => store.state.general.subscriptionPupupFeatureOnly);
		const subscriptionPupupPaymentCompleted = computed(() => store.state.general.subscriptionPupupPaymentCompleted);
		const featureOnly = computed(() => store.state.general.featureOnly);
		const showingSlackPupup = computed(() => store.state.notifications.showingSlackPupup);
		const showingNotificationsPupup = computed(() => store.state.notifications.showingNotificationsPupup);
		const showingNotificationsPupupTitle = computed(() => store.state.notifications.showingNotificationsPupupTitle);
		const showingNotificationsPupupSources = computed(() => store.state.notifications.showingNotificationsPupupSources);

		const isNewSignupUser = computed(() => {
			if (!userCreated.value) return false;
			const createdAt = new Date(userCreated.value).getTime();
			if (!createdAt || Number.isNaN(createdAt)) return false;
			const diff = Date.now() - createdAt;
			return diff >= 0 && diff < 24 * 60 * 60 * 1000;
		});

		// Vuex Mutations
		const updateVar = (payload) => store.commit("general/updateVar", payload);
		const updateUserIdentity = (payload) => store.commit("general/updateUserIdentity", payload);
		const printGlobalMessage = (payload) => store.commit("general/printGlobalMessage", payload);
		const updateLoadingText = (payload) => store.commit("general/updateLoadingText", payload);
		const updateBrandActionUi = (payload) => store.commit("general/updateBrandActionUi", payload);
		const toggleFeatureTsCs = () => store.commit("general/toggleFeatureTsCs");
		const closeSubscriptionPupup = () => {
			TokenService.removeBrandConnectResume();
			store.commit("general/closeSubscriptionPupup");
		};
		const showSubscriptionPupup = (payload) => store.commit("general/showSubscriptionPupup", payload);
		const closeBrandCardPopup = () => store.commit("general/closeBrandCardPopup");
		const closePitchBrandPopup = () => store.commit("general/closePitchBrandPopup");
		const closeCollabHelpPopup = () => store.commit("general/closeCollabHelpPopup");
		const resetBrandActionUi = () => store.commit("general/resetBrandActionUi");
		const closeNotificationsPupup = () => store.commit("notifications/closeNotificationsPupup");

		const closeGlobalIdeaQuiz = () => {
			showGlobalIdeaQuiz.value = false;
		};

		const handleCollabHelpQuiz = () => {
			closeCollabHelpPopup();
			showGlobalIdeaQuiz.value = true;
		};

		const handleBrandConnectClose = (payload) => {
			store.commit("general/setBrandConnectEvent", {
				type: "close",
				payload,
				source: brandConnectModal.value?.source || "",
				createdAt: Date.now(),
			});
		};
		const handleBrandConnectPreview = (payload) => {
			store.commit("general/setBrandConnectEvent", {
				type: "preview-feature",
				payload,
				source: brandConnectModal.value?.source || "",
				createdAt: Date.now(),
			});
		};

		// Vuex Actions
		const loadDashboard = () => store.dispatch("general/loadDashboard");
		const updateBrand = (payload) => store.dispatch("general/updateBrand", payload);
		const formatGlobalToastMessage = (message) => {
			if (typeof message !== "string") return message;
			return message.replace(/<br\s*\/?>/gi, " ");
		};

		const closeGlobalActionUi = ({ restorePreviousMessage = false } = {}) => {
			if (restorePreviousMessage && brandActionUi.value.previousMessageInfo) {
				updateBrandActionUi({
					showPupup: true,
					doAction: "",
					actionBrandId: brandActionUi.value.previousMessageActionBrandId || brandActionUi.value.actionBrandId || null,
					showMessageInfo: brandActionUi.value.previousMessageInfo,
					previousMessageInfo: null,
					previousMessageActionBrandId: null,
				});
				return;
			}
			updateBrandActionUi({
				showPupup: false,
				doAction: "",
				actionBrandId: null,
				showMessageInfo: null,
				previousMessageInfo: null,
				previousMessageActionBrandId: null,
				showReportBrand: null,
				reportMessage: "",
				showBadgeInfo: false,
				badgeInfoKey: "",
				showingToggleShowInfoPupup: false,
				briefWriterRunId: null,
				source: "",
			});
		};

		const runGlobalBrandAction = ({ brandId, action, forceIt }, source = "dashboard") => {
			if (!brandId || !action || !brand.value) return;
			if (action === "connect" && !subscription.value?.allowed?.request) {
				if (featureOnly.value || subscription.value?.featureOnly) {
					error(subscription.value?.notAllowedMessage?.request || "You can't send requests with this type of plan", "Send Request");
					return;
				}
				/*
				if (!subscription.value?.active) {
					showSubscriptionPupup({
						paymentRedirect: brandCardPopup.value?.paymentRedirect || "",
						title: subscription.value?.notAllowedMessage?.request || "",
					});
				} else {
					error(subscription.value?.notAllowedMessage?.request || "You cannot send this request right now.", "Send Request");
				}
				return;
				*/
			}

			const actionActual = getActionBase(action);
			const isImmediateAction = isImmediateBrandAction(action);
			const popupBrand = brandCardPopup.value?.brand || null;
			const popupBrandId = brandCardPopup.value?.brand?.brand_id || null;
			const resolvedBrandId = brandCardPopup.value?.show && popupBrandId ? popupBrandId : brandId;
			if (action === "connect" && !forceIt) {
				const initialData = {};
				const connectPayload = buildConnectActionPayload(
					brandActionUi.value.connectMessage || "",
					brandActionUi.value.partnershipInMind || []
				);

				if (connectPayload.isValid) {
					Object.assign(initialData, connectPayload.data);
				}

				if (popupBrand && String(popupBrand.brand_id) === String(resolvedBrandId)) {
					initialData.requestMeta = {
						brandId: popupBrand.brand_id,
						brandNameVisible: !!popupBrand.show_brand_name,
						paymentRedirect: brandCardPopup.value?.paymentRedirect || "",
						subscriptionTitle: brandCardPopup.value?.subscriptionTitle || subscription.value?.notAllowedMessage?.request || "",
						connecteePreview: {
							brand_id: popupBrand.brand_id,
							brand_name: popupBrand.brand_name || "",
							service: popupBrand.service,
							show_brand_name: !!popupBrand.show_brand_name,
							brand_logo: popupBrand.brand_logo,
							brand_logo_pending: popupBrand.brand_logo_pending,
							brand_logo_is_white: popupBrand.brand_logo_is_white,
							brand_logo_bg_colour: popupBrand.brand_logo_bg_colour,
							brand_logo_has_icons: popupBrand.brand_logo_has_icons,
						},
					};
				}

				updateVar({
					varName: "brandConnectModal",
					info: {
						show: true,
						connecteeId: resolvedBrandId || null,
						initialData: Object.keys(initialData).length ? initialData : null,
						source: source || "dashboard",
					},
				});
				return;
			}
			if (!isImmediateAction && brandActionUi.value.doAction !== actionActual && !forceIt) {
				const previousMessageInfo = brandActionUi.value.showMessageInfo || null;
				updateBrandActionUi({
					source,
					doAction: actionActual,
					actionBrandId: brandId,
					matchBrand: popupBrand && popupBrand.brand_id === brandId ? popupBrand : brandActionUi.value.matchBrand || null,
					showPupup: true,
					showMessageInfo: null,
					previousMessageInfo,
					previousMessageActionBrandId: previousMessageInfo ? brandActionUi.value.actionBrandId || brandId : null,
				});
				return;
			}

			let requestAction = action;
			let data = {};
			if (requestAction === "connect") {
				const connectPayload = buildConnectActionPayload(
					brandActionUi.value.connectMessage || "",
					brandActionUi.value.partnershipInMind || []
				);
				data = connectPayload.data;
				if (brandActionUi.value.briefWriterRunId) {
					data.briefWriterRunId = brandActionUi.value.briefWriterRunId;
				}
				if (!connectPayload.isValid) {
					error("Please write the brand a short message before sending your request.", "Oops");
					return;
				}
			}
			requestAction = toActionRequest(requestAction);
			const messageTitle = getBrandActionMessageTitle(requestAction);

			updateLoadingText(true);
			const request =
				requestAction === "bookmark" || requestAction === "cancel-bookmark"
					? ApiService.post(`brand/bookmark/${brand.value}/${resolvedBrandId}`)
					: ApiService.put(`brands/action/${requestAction}/${brand.value}/${resolvedBrandId}`, data);
			request
				.then((response) => {
					if (response?.data?.message) success(formatGlobalToastMessage(response.data.message), messageTitle);
					if (response?.data?.brand && brandCardPopup.value?.show) {
						store.commit("general/updateBrandCardPopup", { brand: response.data.brand });
					}
					if (shouldClosePopupForAction(requestAction)) {
						closeBrandCardPopup();
					}
					closeGlobalActionUi();
					loadDashboard();
					updateLoadingText();
				})
				.catch((err) => {
					updateLoadingText();
					const messageInfo = err?.response?.data?.message;
					if (messageInfo?.subscribe) {
						showSubscriptionPupup({
							paymentRedirect: brandCardPopup.value?.paymentRedirect || "",
							title: messageInfo?.message || "",
						});
						return;
					}
					if (messageInfo?.message) {
						error(messageInfo.message, "Oops");
						return;
					}
					error(general.getErrorMessage(err) || "Unable to complete this action at the moment.", "Oops");
				});
		};

		const { viewMessage, reportBrand, badgeInfo, toggleShowInfo, subscribe } = useBrandPopupHandlers({
			api: ApiService,
			toastError: error,
			updateLoadingText,
			brandAction: ({ brandId, action, forceIt }) => runGlobalBrandAction({ brandId, action, forceIt }, "dashboard"),
			setShowMessageInfo: (value) => updateBrandActionUi({ showMessageInfo: value }),
			setActionBrandId: (value) => updateBrandActionUi({ actionBrandId: value }),
			setShowPupup: (value) => updateBrandActionUi({ showPupup: value }),
			setShowReportBrand: (value) => updateBrandActionUi({ showReportBrand: value }),
			setShowBadgeInfo: (value) => updateBrandActionUi({ showBadgeInfo: !!value?.show, badgeInfoKey: value?.key || "" }),
			setShowToggleShowInfoPupup: (value) => updateBrandActionUi({ showingToggleShowInfoPupup: value }),
			showSubscriptionPupup: (payload) => showSubscriptionPupup(payload),
			paymentRedirect: computed(() => brandCardPopup.value?.paymentRedirect || ""),
			subscriptionTitle: computed(() => brandCardPopup.value?.subscriptionTitle || ""),
		});

		watch(brandCardPopupEvent, (event) => {
			if (!event) return;
			if (isBrandsPopupSource(event.source)) {
				return;
			}
			const source = resolvePopupSource(event.source, "dashboard");
			updateBrandActionUi({ source });
			const handlers = {
				action: () => {
					if (!event.payload?.action) return;
					runGlobalBrandAction(event.payload || {}, source);
				},
				viewMessage: () => viewMessage(event.payload),
				reportBrand: () => reportBrand(event.payload),
				badgeInfo: () => badgeInfo(event.payload),
				toggleShowInfo: () => toggleShowInfo(event.payload),
				subscribe: () => subscribe(event.payload),
				close: () => {
					closeGlobalActionUi();
					closeBrandCardPopup();
				},
			};
			if (handlers[event.type]) handlers[event.type]();
			store.commit("general/clearBrandCardPopupEvent");
		});

		watch(brandConnectEvent, (event) => {
			if (!event || isBrandsPopupSource(event.source) || event.source === "pitch") return;
			const source = resolvePopupSource(event.source, "dashboard");
			if (event.type === "close" && event.payload) {
				updateBrandActionUi({
					source,
					connectMessage: event.payload.message || "",
					partnershipInMind: event.payload.partnerships || [],
					briefWriterRunId: event.payload.briefWriterRunId || null,
				});
				runGlobalBrandAction({ brandId: event.payload.brandId, action: "connect", forceIt: true }, source);
				store.commit("general/clearBrandConnectEvent");
			}
		});

		const submitGlobalReport = () => {
			const reportBrandId = brandActionUi.value.showReportBrand;
			const reportMessage = (brandActionUi.value.reportMessage || "").trim();
			if (!reportBrandId || !brand.value) return;
			if (!reportMessage) {
				error("Please tell us why you are reporting this brand.", "Oops");
				return;
			}
			updateLoadingText(true);
			ApiService.post(`brand/report/${brand.value}/${reportBrandId}`, { message: reportMessage })
				.then((response) => {
					success(response?.data?.message || "Thanks for your report.", "Report Brand");
					updateBrandActionUi({ showReportBrand: null, reportMessage: "" });
					updateLoadingText();
				})
				.catch((err) => {
					updateLoadingText();
					error(general.getErrorMessage(err) || "Unable to submit report right now.", "Oops");
				});
		};

		watch(brandActionEvent, (event) => {
			if (!event) return;
			if (isBrandsPopupSource(event.source)) {
				return;
			}
			const source = resolvePopupSource(event.source, "dashboard");
			const handlers = {
				actioner: () => {
					if (!event.payload?.action) return;
					runGlobalBrandAction(event.payload || {}, source);
				},
				closePupup: () => closeGlobalActionUi({ restorePreviousMessage: !!event.payload?.restorePreviousMessage }),
				submitReportBrand: () => submitGlobalReport(),
				closeReportPupup: () => updateBrandActionUi({ showReportBrand: null, reportMessage: "" }),
				closeBadgeInfoPupup: () => updateBrandActionUi({ showBadgeInfo: false, badgeInfoKey: "" }),
				toggledShowInfo: () => updateBrandActionUi({ showingToggleShowInfoPupup: false }),
			};
			if (handlers[event.type]) handlers[event.type]();
			store.commit("general/clearBrandActionEvent");
		});

		const isValidReturnTo = (value) => {
			return typeof value === "string" && value.startsWith("/") && !value.startsWith("/opportunities") && !value.startsWith("/pitch");
		};

		const getValidReturnTo = () => {
			const candidate = route && route.query ? route.query.returnTo : null;
			if (Array.isArray(candidate)) {
				return isValidReturnTo(candidate[0]) ? candidate[0] : null;
			}
			return isValidReturnTo(candidate) ? candidate : null;
		};

		watch(
			() => ({ path: route.path, params: route.params, meta: route.meta }),
			({ params, meta }) => {
				const isPitchFocus = !!params?._id && meta && meta.type === "pitch" && !meta.jump && !meta.paymentProcessing;
				if (isPitchFocus) {
					const pitchId = params._id;
					const returnTo = getValidReturnTo();
					const openPitchId = pitchBrandPopup.value?.pitchId;
					if (
						!pitchBrandPopup.value?.show ||
						String(openPitchId || "") !== String(pitchId || "") ||
						pitchBrandPopup.value?.source !== "route"
					) {
						store.commit("general/openPitchBrandPopup", {
							pitchId,
							source: "route",
							returnTo,
						});
					}
					return;
				}

				if (pitchBrandPopup.value?.show && pitchBrandPopup.value?.source === "route") {
					closePitchBrandPopup();
				}
			},
			{ immediate: true }
		);

		// Computed Properties
		const isPublic = computed(() => route.matched.some((record) => record.meta.public));

		const showPostHog = computed(() => {
			let extra = TokenService.getExtraInfo();
			const showAdmin = extra && extra.access && extra.access === "admin";
			return typeof posthog !== "undefined" && (showAdmin || general.isDev());
		});

		const headerClasses = computed(() => route.meta.headerClasses || {});
		const footerClasses = computed(() => route.meta.footerClasses || {});

		const hasTopMessage = computed(() => {
			if (!isPublic.value && globalMessage.value !== "") {
				if (globalMessageMeta.value && globalMessageMeta.value.pageNames) {
					const pageNames = globalMessageMeta.value.pageNames.split(",");
					for (let i = 0; i < pageNames.length; i++) {
						if (route.name === pageNames[i]) return true;
					}
					return false;
				}
				if (globalMessageMeta.value && globalMessageMeta.value.hiddenOn) {
					const hiddenOn = globalMessageMeta.value.hiddenOn;
					for (let i = 0; i < hiddenOn.length; i++) {
						if (route[hiddenOn[i].type] === hiddenOn[i].match) return false;
					}
				}
				return true;
			}
			return false;
		});
		const hasVisibleTopMessage = computed(() => hasTopMessage.value && topMessageVisible.value);

		const routerViewKey = computed(() => {
			if (route.meta && route.meta.viewKey) return route.meta.viewKey;
			return route.meta && route.meta.nameAsKey ? route.name : route.fullPath;
		});
		const brandId = computed(() => brand.value);
		const showSubscriptionRequired = computed(() => subscription.value && !subscription.value.active);

		const resumeBrandConnectAfterUpgrade = () => {
			const pendingResume = TokenService.getBrandConnectResume();
			if (!pendingResume) return;
			if (!subscription.value?.active || subscription.value?.featureOnly || !subscription.value?.allowed?.request) return;
			if (!!pendingResume.userBrandId && Number(pendingResume.userBrandId) !== Number(brandId.value)) return;

			updateVar({
				varName: "brandConnectModal",
				info: {
					show: true,
					connecteeId: pendingResume.connecteeId || null,
					initialData: pendingResume.initialData || null,
					source: pendingResume.source || "brands",
				},
			});

			TokenService.removeBrandConnectResume();
		};

		// Methods
		const sendPostHogEvent = () => {
			if (showPostHog.value) {
				if (posthogEventText.value) {
					general.posthogCapture("issue-stamp", { info: posthogEventText.value });
					posthogEventText.value = "";
					showPostHogEvent.value = false;
					success("Issue sent");
				} else {
					error("Please enter a description");
				}
			}
		};

		const loadVideos = (e) => {
			e.preventDefault();
			updateVar({ varName: "showVideos", info: true });
		};

		const closeVideos = () => {
			updateVar({ varName: "showVideos", info: false });
			updateVar({ varName: "singleVideo", info: false });
			updateVar({ varName: "videoTitle", info: "" });
			general.modalClose();
		};

		const closeSelectedInspiration = () => {
			updateVar({ varName: "selectedInspiration", info: null });
		};

		const closeAllModalsForLogout = () => {
			updateVar({
				varName: "brandConnectModal",
				info: {
					show: false,
					connecteeId: null,
					initialData: null,
					source: "",
				},
			});
			closeBrandCardPopup();
			closePitchBrandPopup();
			closeCollabHelpPopup();
			resetBrandActionUi();
			updateVar({ varName: "showFeatureTsCs", info: false });
			updateVar({ varName: "showWelcome", info: false });
			updateVar({ varName: "showVideos", info: false });
			updateVar({ varName: "selectedInspiration", info: null });
			closeSubscriptionPupup();
			closeNotificationsPupup();
			showWelcomePopup.value = "";
			showUpdatesPopup.value = false;
			showGlobalIdeaQuiz.value = false;
			showPostHogEvent.value = false;
		};

		const showHideHeaderFooter = (name) => {
			updateVar({ varName: "showHeader", info: !isPublic.value || !!route.meta.showHeader });
			updateVar({ varName: "showFooter", info: !isPublic.value || !!route.meta.showFooter });
		};

		const setFullMenu = () => {
			TokenService.saveFullMenu(!fullMenu.value);
			updateVar({ varName: "fullMenu", info: !fullMenu.value });
		};

		const toggleMobileMenu = (state) => {
			state = typeof state !== "undefined" ? state : !mobileMenu.value;
			updateVar({ varName: "mobileMenu", info: state });
		};

		const menuLinkClick = () => {
			subMenuClick.value = true;
			setTimeout(() => {
				subMenuClick.value = false;
			}, 1000);
		};

		const closeTopMessage = () => {
			topMessageVisible.value = false;
			printGlobalMessage({ message: "" });
		};

		const setTopMessageVisible = (value) => {
			topMessageVisible.value = !!value;
		};

		const resendConfirmEmail = () => {
			updateLoadingText("One moment...");
			ApiService.get("users/confirm-email/resend")
				.then((info) => {
					updateLoadingText();
					success(info.data, "Confirm Email");
					closeTopMessage();
				})
				.catch(() => {
					updateLoadingText();
				});
		};

		const closePupup = () => {
			updateVar({ varName: "showWelcome", info: false });
			welcomeIsHelp.value = false;
			pupupPage.value = 1;
			ApiService.post(`users/action/welcome`);
		};

		const showHelp = () => {
			if (showWelcome.value) return;
			welcomeIsHelp.value = true;
			updateVar({ varName: "showWelcome", info: true });
		};

		const loginCheck = () => {
			if (needsLoginCheck.value === false || !!isPublic.value) return;
			needsLoginCheck.value = false;
			if (route.params.userBrand && route.params.userBrand !== brand.value) {
				updateBrand(route.params.userBrand);
			}
			updateLoadingText(true);
			general.loginCheck(ApiService, (ok, userInfo) => {
				showPage.value = ok;
				if (ok) {
					updateUserIdentity({
						initials: `${(userInfo.fname || "").substr(0, 1)}${(userInfo.sname || "").substr(0, 1)}`.toUpperCase(),
						name: userInfo.fname || "",
						firstName: userInfo.fname || "",
					});
					updateVar({ varName: "show_newbie_pupup", info: !!userInfo.showNewbiePopup });
					updateVar({ varName: "hasOpenedWritingTool", info: !!userInfo.hasOpenedWritingTool });
					updateVar({ varName: "featureOnly", info: !!userInfo.featureOnly });
					showWelcomePopup.value = userInfo.showWelcome || "";
					showWelcomeOpenerMessage.value = userInfo.showWelcomeOpenerMessage || "";
					if (userInfo.updatesPopupContent) {
						showUpdatesPopup.value = true;
						updatesPopupContent.value = userInfo.updatesPopupContent;
					}
					updateVar({ varName: "welcomeMessage", info: userInfo.welcomeMessage });
					posthog.identify(userInfo.id, { email: userInfo.email, company_name: userInfo.company_name });
					updateBrand(userInfo.brandId);
					loadDashboard();
					updateVar({ varName: "userCreated", info: userInfo.created || null });
					updateVar({ varName: "userLoaded", info: true });
				} else {
					store.dispatch("auth/logoutForce", {
						reason: "session-expired",
						redirect: `/login?redirect=${encodeURIComponent(route.fullPath || window.location.pathname)}`,
					});
				}
				updateLoadingText();
			});
		};

		// Lifecycle & Watchers
		watch(
			route,
			(to, from) => {
				// Skip if route meta is not yet populated (happens on initial load with immediate: true)
				if (!to.name || !to.matched.length) {
					return;
				}
				if (to.name.startsWith("login") || to.path.startsWith("/login")) {
					closeAllModalsForLogout();
				}
				showHideHeaderFooter(to.name);
				closeNotificationsPupup();

				if (to.meta.public) {
					showPage.value = true;
					needsLoginCheck.value = false; // Public routes don't need login check
				} else {
					needsLoginCheck.value = true;
					loginCheck();
				}

				// Handle routes that trigger subscription payment UI
				if (to.meta.paymentProcessing && to.name !== "account") {
					const paymentRedirect = to.params.checkoutSessionId
						? to.path.split("/" + to.params.checkoutSessionId)[0]
						: to.path;
					showSubscriptionPupup({ paymentRedirect });
				}
				if (to.meta.flushCheckoutSessions) {
					ApiService.delete("stripe/checkout-sessions");
				}
				if (!!from && to.path !== from.path) {
					closeBrandCardPopup();
					resetBrandActionUi();
					window.scrollTo(0, 0);
				}
			},
			{ immediate: true } // Ensure this runs immediately for the initial route
		);

		watch(brandId, (newBrand, oldBrand) => {
			if (newBrand !== oldBrand && oldBrand !== "default") {
				loadDashboard();
			}
		});

		watch(checkMatchRunUpdateMessage, (newVal) => {
			if (newVal) {
				if (!isNewSignupUser.value) {
					success(checkMatchRunUpdateMessage.value, "Matches Updated");
				}
				updateVar({ varName: "checkMatchRunUpdateMessage", info: "" });
			}
		});

		watch(subscriptionPupupPaymentCompleted, (newVal, oldVal) => {
			if (!newVal || newVal === oldVal) return;
			resumeBrandConnectAfterUpgrade();
		});

		watch(
			() => subscription.value?.active,
			(isActive, wasActive) => {
				if (!isActive || isActive === wasActive) return;
				resumeBrandConnectAfterUpgrade();
			}
		);

		// Logic from created()
		document.title = siteName.value;
		showHideHeaderFooter(route.name);
		if (window.location.hash === "#email-verified") {
			setTimeout(() => {
				success("Your email has been verified", "Verification");
				window.location.hash = "";
			}, 1000);
		}

		// Initial route handling is now done by the route watcher with { immediate: true }

		// Logic from beforeMount() - only non-route dependent setup
		onBeforeMount(() => {
			// Don't check login status here - let the route watcher handle it
			// because route.meta might not be fully populated yet
			let fM = TokenService.getFullMenu() === false || TokenService.getFullMenu() === "false" ? false : true;
			if (fullMenu.value !== fM) {
				updateVar({ varName: "fullMenu", info: fM });
			}
			let gm = TokenService.getGlobalMessage();
			printGlobalMessage(gm);
			let tableSortingInfo = TokenService.getTableSorting();
			updateVar({ varName: "tableSortingInfo", info: tableSortingInfo });
			let tableHeadingVisibility = TokenService.getTableHeadingVisibility();
			updateVar({ varName: "tableHeadingVisibility", info: tableHeadingVisibility });
		});

		return {
			// from data
			showPage,
			subMenuClick,
			pupupPage,
			pupupPages,
			welcomeIsHelp,
			showWelcomeOpenerMessage,
			posthogEventText,
			showPostHogEvent,
			hideSubscriptionPupup,
			showWelcomePopup,
			showGlobalIdeaQuiz,
			// from methods
			setFullMenu,
			toggleMobileMenu,
			menuLinkClick,
			showHelp,
			resendConfirmEmail,
			closeTopMessage,
			setTopMessageVisible,
			closePupup,
			loadVideos,
			closeVideos,
			closeSelectedInspiration,
			closeSubscriptionPupup,
			closeCollabHelpPopup,
			closeGlobalIdeaQuiz,
			handleCollabHelpQuiz,
			toggleFeatureTsCs,
			closeNotificationsPupup,
			handleBrandConnectClose,
			handleBrandConnectPreview,
			sendPostHogEvent,
			// from computed / vuex
			loadingText,
			showHeader,
			showFooter,
			showingModal,
			siteName,
			fullMenu,
			mobileMenu,
			hasTopMessage,
			hasVisibleTopMessage,
			globalMessage,
			globalMessageColour,
			globalMessageMeta,
			routerViewKey,
			showWelcome,
			isPublic,
			showSubscriptionRequired,
			subscription,
			brandConnectModal,
			collabHelpPopup,
			selectedInspiration,
			showingSubscriptionPupup,
			subscriptionPupupPaymentRedirect,
			subscriptionPupupFeatureOnly,
			subscriptionPupupPaymentCompleted,
			subscriptionPupupTitle,
			featureOnly,
			showFeatureTsCs,
			showingNotificationsPupupTitle,
			showingNotificationsPupup,
			showingNotificationsPupupSources,
			showVideos,
			showingSlackPupup,
			showPostHog,
			headerClasses,
			footerClasses,
			showUpdatesPopup,
			updatesPopupContent,
		};
	},
};
</script>

<style lang="less">
@import "vue-select/dist/vue-select.css";
@import "./assets/css/less/_helper.less";
@import "./assets/css/less/_settings.less";
@import "./assets/css/less/_global.less";
@imgRoot: "../../../";
@import "./assets/css/less/components/_vue-select-override.less";
@import "./assets/css/less/components/_section.less";
@import "./assets/css/less/components/_buttons.less";
@import "./assets/css/less/components/_forms.less";
@import "./assets/css/less/components/_content-header.less";
@import "./assets/css/less/components/_window-actions.less";
@import "./assets/css/less/components/_info-panel.less";
@import "./assets/css/less/components/_update-actions.less";
@import "./assets/css/less/components/_vue-toastification.less";
.internal-loading-spinner {
	background-color: @surfacePanel;
	padding: 20px;
	border-radius: @radiusXl;
	min-height: 30vh;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50%;
	margin: 50px auto 0;
}
.feature-terms-list {
	padding: 0 0 0 20px;
	li {
		margin-bottom: 10px;
		a {
			color: @guidedRose;
		}
		&:last-child {
			margin-bottom: 0;
		}
	}
}
.posthog-menu-item {
	position: fixed;
	left: 6px;
	bottom: 6px;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	width: 14px;
	z-index: 99999999999;
	> span {
		width: 14px;
		height: 14px;
		background-color: @red;
		.rounded(50%);
		cursor: pointer;
		display: block;
	}
	.posthog-event-text {
		display: none;
		margin-left: 10px;
		width: 220px;
	}
	&.show {
		width: auto;
		.posthog-event-text {
			display: block;
		}
	}
}

.welcome-panels-wrap {
	padding: 0;

	.buttons {
		text-align: right;

		.button {
			margin-left: 20px;
		}
	}
	.video-guides {
		display: flex;
		cursor: pointer;
		margin-top: 25px;
		p {
			color: @guidedRose;
			font-size: 20px;
			font-weight: @bold;
		}
		img {
			width: 100px;
			height: auto;
			margin-right: 10px;
		}
		&:hover {
			opacity: 0.8;
		}
	}
}

.pupup-link {
	color: @guidedRose;
	font-weight: @bold;
}

.welcome-panels {
	.welcome-panel {
		.brown {
			color: @guidedRose;
			font-size: 24px;
			margin: 0;
		}

		h3 {
			font-size: 20px;
			margin: 10px 0 5px;
			color: @formDefaultColour;
		}

		hr {
			margin: 11px 0;
		}

		p {
			margin: 0;
			padding: 0;
			line-height: 1.5;

			a {
				color: @guidedRose;
				font-weight: @bold;
			}

			&.opener {
				margin: 0 0 20px;
				&.highlighted {
					background-color: @guidedRose;
					color: white;
					font-size: 20px;
					padding: 5px 10px;
					.rounded(3px);
					a {
						color: inherit;
					}
				}
			}
		}

		&.panel2 {
			p {
				margin-bottom: 20px;
			}

			h2 {
				margin-bottom: 10px;
			}
		}

		.subscription-required {
			font-size: 0.7em;
			background-color: @guidedRose;
			color: white;
			display: inline-block;
			padding: 0 5px;
			.rounded(3px);
			margin-bottom: 5px;
		}

		.list-item {
			padding: 0 0 0 34px;
			position: relative;

			.svg {
				width: 25px;
				height: 25px;
				position: absolute;
				left: 0;
			}

			h2 {
				display: block;
				color: @formDefaultColour;
				margin: 0 0 2px;
				padding: 0;
				font-size: 20px;
				position: relative;
			}

			p {
				margin: 0;
				padding: 0;
			}
		}
	}

	.call {
		color: @guidedRose;
		margin: 20px 0 0 !important;
		a {
			color: inherit;
		}
	}
}
@media screen and (min-width: 960px) {
	.welcome-panels {
		.welcome-panel {
			hr {
				margin: 11px 0 11px;
				display: none;
			}

			.list-items {
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;

				.list-item {
					flex: 1 1 30%; /* Grow, Shrink, Basis */
					margin: 5px 5px 0;
					box-sizing: border-box;
					padding-bottom: 20px;
				}
			}
		}
	}
	.welcome-panels-wrap {
		position: relative;

		.video-guides {
			position: absolute;
			bottom: 0px;
			margin: 0;
		}
	}
}
</style>
